import type { TargetedAnimationEvent, TargetedPointerEvent } from "preact";
import { useLayoutEffect, useRef } from "preact/hooks";

import type { LauncherPoint } from "./placement";

/** 挂载根交给入口的定位能力：吸附几何、记忆和重新定位都归挂载根。 */
export interface LauncherDrag {
  /** 视口坐标下的落点吸到边上以后的位置，拖动时摆落点虚框。 */
  snap: (point: LauncherPoint) => LauncherPoint;
  /** 松手时交出投影落点，挂载根吸附、记住并把入口挪过去。 */
  place: (point: LauncherPoint) => void;
}

// 参数照设计稿 Shop Agent Drag & Snap：跟手 k700 ζ.9，飞行 k160 ζ.6。
const FOLLOW_STIFFNESS = 700;
const FOLLOW_DAMPING = 0.9;
const FLIGHT_STIFFNESS = 160;
const FLIGHT_DAMPING = 0.6;
/** 位移不到这么多就松手算点按，照常打开聊天窗。 */
const TAP_SLOP_PX = 6;
/** 按松手速度往前投影的秒数，用力甩一下就能横跨屏幕。 */
const PROJECT_SECONDS = 0.12;
const THROW = 0.35;
/** 指针停下这么久，停手前的速度就不算数了。 */
const STILL_MS = 100;

type Phase = "held" | "dragging" | "flying" | "landing";

interface Motion {
  phase: Phase;
  /** 按下时入口还在飞：点按也要按当前位置重新吸附。 */
  caught: boolean;
  pointerId: number;
  toggle: HTMLElement;
  character: HTMLElement;
  eyes: HTMLElement | null;
  reduced: boolean;
  position: LauncherPoint;
  velocity: LauncherPoint;
  target: LauncherPoint;
  grab: LauncherPoint;
  press: LauncherPoint;
  pointer: LauncherPoint;
  pointerTime: number;
  pointerVelocity: LauncherPoint;
  /** 当前写在入口上的位移；入口的停靠位置 = 实测位置减去它。 */
  offset: LauncherPoint;
  tilt: number;
  scale: number;
  lift: number;
  gaze: LauncherPoint;
  time: number;
  frame: number;
}

function project(point: LauncherPoint, velocity: LauncherPoint): LauncherPoint {
  return {
    x: point.x + velocity.x * PROJECT_SECONDS,
    y: point.y + velocity.y * PROJECT_SECONDS,
  };
}

/** 指针事件只在移动时更新速度，按住不动再松手不能按停手前的速度甩出去。 */
function throwVelocity(current: Motion, now: number): LauncherPoint {
  const fade = Math.min(
    1,
    Math.max(0, 1 - (now - current.pointerTime) / STILL_MS),
  );
  return {
    x: current.pointerVelocity.x * fade,
    y: current.pointerVelocity.y * fade,
  };
}

function clamp(value: number, limit: number): number {
  return Math.max(-limit, Math.min(limit, value));
}

function px({ x, y }: LauncherPoint): string {
  return `${x.toFixed(2)}px ${y.toFixed(2)}px`;
}

/**
 * 收起入口的拖拽换位：按下即拾起，弹簧跟手并随速度倾斜，松手按投影吸到左右边再弹簧飞过去，落地挤压一下。
 * 每帧直接写样式不重新渲染。松手时入口已经换到新位置，飞行按每帧实测的停靠位置收敛，不假设挂载根怎么摆。
 */
export function useLauncherDrag(drag: LauncherDrag | undefined) {
  const launcher = useRef<HTMLDivElement>(null);
  const ghost = useRef<HTMLDivElement>(null);
  const dragged = useRef(false);
  const latest = useRef(drag);
  latest.current = drag;
  const motion = useRef<Motion | undefined>(undefined);
  const enabled = drag !== undefined;

  const stop = () => {
    const current = motion.current;
    if (!current) return;
    cancelAnimationFrame(current.frame);
    motion.current = undefined;
    launcher.current?.style.removeProperty("translate");
    ghost.current?.style.removeProperty("opacity");
    current.toggle.removeAttribute("data-held");
    for (const name of ["rotate", "scale", "--gn-character-lift"])
      current.character.style.removeProperty(name);
    current.eyes?.style.removeProperty("translate");
  };

  useLayoutEffect(() => (enabled ? stop : undefined), [enabled]);

  const tick = (now: number) => {
    const current = motion.current;
    const mover = launcher.current;
    if (!current || !mover) return;
    const dt = Math.min(0.032, Math.max(0.001, (now - current.time) / 1000));
    current.time = now;
    const box = current.toggle.getBoundingClientRect();
    const rest = {
      x: box.left - current.offset.x,
      y: box.top - current.offset.y,
    };
    const held = current.phase === "held" || current.phase === "dragging";
    const target = held ? current.target : rest;
    const stiffness = held ? FOLLOW_STIFFNESS : FLIGHT_STIFFNESS;
    const damping = held
      ? FOLLOW_DAMPING
      : current.reduced
        ? 1
        : FLIGHT_DAMPING;
    const friction = 2 * Math.sqrt(stiffness) * damping;
    for (const axis of ["x", "y"] as const) {
      current.velocity[axis] +=
        (-stiffness * (current.position[axis] - target[axis]) -
          friction * current.velocity[axis]) *
        dt;
      current.position[axis] += current.velocity[axis] * dt;
    }
    const ease = (rate: number) => Math.min(1, dt * rate);
    const tilt =
      current.phase !== "landing" && !current.reduced
        ? clamp(current.velocity.x * 0.014, 14)
        : 0;
    current.tilt += (tilt - current.tilt) * ease(12);
    current.scale += ((held ? 1.08 : 1) - current.scale) * ease(14);
    current.lift += ((held ? 1 : 0) - current.lift) * ease(12);
    current.gaze.x +=
      (clamp(current.velocity.x * 0.006, 4) - current.gaze.x) * ease(10);
    current.gaze.y +=
      (clamp(current.velocity.y * 0.006, 3) - current.gaze.y) * ease(10);
    current.offset = {
      x: current.position.x - rest.x,
      y: current.position.y - rest.y,
    };
    mover.style.translate = px(current.offset);
    current.character.style.rotate = `${current.tilt.toFixed(2)}deg`;
    current.character.style.scale = current.scale.toFixed(3);
    current.character.style.setProperty(
      "--gn-character-lift",
      current.lift.toFixed(3),
    );
    current.eyes?.style.setProperty("translate", px(current.gaze));
    const preview = ghost.current;
    if (preview) {
      const dragging = current.phase === "dragging";
      preview.style.opacity = dragging ? "1" : "";
      if (dragging && latest.current)
        preview.style.translate = px(
          latest.current.snap(
            project(current.target, throwVelocity(current, now)),
          ),
        );
    }
    if (
      current.phase === "flying" &&
      Math.abs(current.position.x - rest.x) < 3 &&
      Math.abs(current.position.y - rest.y) < 12
    ) {
      current.phase = "landing";
      if (!current.reduced) {
        // 连续落地要重播挤压，先摘掉属性强制重排再挂回去。重排时还拿着，收角、长尾巴的过渡要等挂回去以后才开始。
        current.toggle.removeAttribute("data-landing");
        current.toggle.getBoundingClientRect();
        current.toggle.setAttribute("data-landing", "");
      }
      current.toggle.removeAttribute("data-held");
    } else if (
      current.phase === "landing" &&
      Math.hypot(current.position.x - rest.x, current.position.y - rest.y) <
        0.6 &&
      Math.hypot(current.velocity.x, current.velocity.y) < 6
    ) {
      stop();
      return;
    }
    current.frame = requestAnimationFrame(tick);
  };

  const onPointerDown = (event: TargetedPointerEvent<HTMLElement>) => {
    if (!latest.current || !event.isPrimary || event.button !== 0) return;
    const toggle = event.currentTarget;
    const character = toggle.querySelector<HTMLElement>(".gn-character");
    if (!character) return;
    toggle.setPointerCapture(event.pointerId);
    dragged.current = false;
    const now = performance.now();
    const previous = motion.current;
    const box = toggle.getBoundingClientRect();
    const position = previous?.position ?? { x: box.left, y: box.top };
    const pointer = { x: event.clientX, y: event.clientY };
    motion.current = {
      phase: "held",
      caught: previous !== undefined,
      pointerId: event.pointerId,
      toggle,
      character,
      eyes: character.querySelector<HTMLElement>(".gn-character-eyes"),
      reduced: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
      position,
      velocity: previous?.velocity ?? { x: 0, y: 0 },
      target: { ...position },
      grab: { x: pointer.x - position.x, y: pointer.y - position.y },
      press: pointer,
      pointer,
      pointerTime: now,
      pointerVelocity: { x: 0, y: 0 },
      offset: previous?.offset ?? { x: 0, y: 0 },
      tilt: previous?.tilt ?? 0,
      scale: previous?.scale ?? 1,
      lift: previous?.lift ?? 0,
      gaze: previous?.gaze ?? { x: 0, y: 0 },
      time: previous?.time ?? now,
      frame: previous?.frame ?? requestAnimationFrame(tick),
    };
    toggle.removeAttribute("data-landing");
    toggle.setAttribute("data-held", "");
    const preview = ghost.current;
    if (preview) {
      // 虚框出现前先无过渡地摆到当前落点，不从视口左上角滑进来。
      preview.style.transition = "none";
      preview.style.translate = px(latest.current.snap(position));
      preview.getBoundingClientRect();
      preview.style.removeProperty("transition");
    }
  };

  const onPointerMove = (event: TargetedPointerEvent<HTMLElement>) => {
    const current = motion.current;
    if (
      !current ||
      current.pointerId !== event.pointerId ||
      (current.phase !== "held" && current.phase !== "dragging")
    )
      return;
    const now = performance.now();
    const seconds = Math.max(1, now - current.pointerTime) / 1000;
    current.pointerVelocity = {
      x:
        current.pointerVelocity.x * 0.6 +
        ((event.clientX - current.pointer.x) / seconds) * 0.4,
      y:
        current.pointerVelocity.y * 0.6 +
        ((event.clientY - current.pointer.y) / seconds) * 0.4,
    };
    current.pointer = { x: event.clientX, y: event.clientY };
    current.pointerTime = now;
    if (
      Math.hypot(
        event.clientX - current.press.x,
        event.clientY - current.press.y,
      ) > TAP_SLOP_PX
    )
      current.phase = "dragging";
    current.target = {
      x: event.clientX - current.grab.x,
      y: event.clientY - current.grab.y,
    };
  };

  const release = (event: TargetedPointerEvent<HTMLElement>) => {
    const current = motion.current;
    if (
      !current ||
      current.pointerId !== event.pointerId ||
      (current.phase !== "held" && current.phase !== "dragging")
    )
      return;
    const moved = current.phase === "dragging";
    const velocity = throwVelocity(current, performance.now());
    current.phase = "flying";
    if (moved || current.caught)
      latest.current?.place(
        moved ? project(current.target, velocity) : current.position,
      );
    if (!moved) return;
    current.velocity.x += velocity.x * THROW;
    current.velocity.y += velocity.y * THROW;
    // 松手后浏览器在同一轮事件里派发 click，拖完不能顺带打开聊天窗。
    dragged.current = true;
    setTimeout(() => {
      dragged.current = false;
    });
  };

  return {
    launcher,
    ghost,
    /** 刚拖完，这次 click 不算点按。 */
    dragged,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: release,
      onPointerCancel: release,
      onLostPointerCapture: release,
      onAnimationEnd: (event: TargetedAnimationEvent<HTMLElement>) => {
        if (event.animationName === "gnLauncherLand")
          event.currentTarget.removeAttribute("data-landing");
      },
    },
  };
}
